import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import collect from 'collect.js';
import CurrencyInput from '../../components/form/CurrencyInput';
import { useSelector } from 'react-redux';
import { RefundReason } from '../../services/exports/Constants';
import useFeedbackHandler from '../../hooks/useFeedbackHandler';
import RefundsManager from '../../services/api/RefundsManager';
import { toast } from 'react-toastify';
import RefundIcon from '../../resources/assets/icons/RefundIcon';
import { useOngoingOrders } from '../../providers/OngoingOrdersProvider';
import { useHelpers } from '../../providers/HelpersProvider';
import { Button, Divider, Form, Input, Modal, Select } from 'antd';

export default function CreateRefundModal({ payment, toggleModal }) {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:Order:CreateRefundModal' });

  const { formatCurrency } = useHelpers();
  const { refreshSelectedOrder } = useOngoingOrders();
  const { setFeedback, resetFeedback, renderFeedbackLabel } = useFeedbackHandler();

  const { company } = useSelector((state) => state.company);
  const [amount, setAmount] = useState(null);
  const [reason, setReason] = useState(RefundReason.RequestedByCustomer);
  const [manualReason, setManualReason] = useState('');
  const [loading, setLoading] = useState(false);

  const availableAmount = useMemo(
    () =>
      Math.round(
        (payment.total_amount -
          payment.frozen_amount +
          collect(payment?.adjustments).sum('total_amount') +
          Number.EPSILON) *
          100,
      ) / 100,
    [payment],
  );

  const refundDisabled = useMemo(
    () =>
      loading || payment.frozen_amount > 0 || !availableAmount || !amount || amount > availableAmount || +amount === 0,
    [loading, payment.frozen_amount, amount, availableAmount],
  );

  useEffect(() => observeAmount(), [payment.frozen_amount, amount, availableAmount]);

  function observeAmount() {
    if (payment.frozen_amount > 0) {
      return setFeedback({
        type: 'warning',
        message: t('feedback.payment_frozen', {
          provider: company?.service_provider?.name,
        }),
      });
    }

    if (+amount === 0) {
      return resetFeedback();
    }

    if (amount > availableAmount) {
      return setFeedback({
        type: 'warning',
        message: t('feedback.amount_too_big', {
          amount: formatCurrency(availableAmount),
        }),
      });
    }

    return resetFeedback();
  }

  async function refund() {
    setLoading(true);
    const { success, data } = await RefundsManager.create(payment?.id, {
      amount,
      reason: reason === RefundReason.Other ? manualReason : reason,
    });
    setLoading(false);

    if (!success) {
      return setFeedback({
        message: data?.message,
        type: 'error',
        errors: data?.errors,
      });
    }

    refreshSelectedOrder();

    toast(
      <div className="flex">
        <RefundIcon className="mr-2" />
        <span>
          <Trans
            t={t}
            values={{
              amount: formatCurrency(amount),
            }}
          >
            toasts.refund_successful
          </Trans>
        </span>
      </div>,
    );

    toggleModal();
  }

  const reasons = [
    {
      label: t('form.reason.options.requested_by_customer'),
      value: RefundReason.RequestedByCustomer,
    },
    {
      label: t('form.reason.options.reclamation'),
      value: RefundReason.Reclamation,
    },
    {
      label: t('form.reason.options.communication_error'),
      value: RefundReason.CommunicationError,
    },
    {
      label: t('form.reason.options.other'),
      value: RefundReason.Other,
    },
  ];

  return (
    <Modal
      open
      centered
      title={t('title')}
      onCancel={() => toggleModal()}
      footer={
        <>
          <Divider className="my-3" />
          <div className="flex justify-end gap-md">
            <Button onClick={() => toggleModal()} disabled={loading}>
              {t('buttons.cancel')}
            </Button>
            <Button danger type="primary" onClick={refund} loading={loading} disabled={refundDisabled}>
              {t('buttons.refund')}
            </Button>
          </div>
        </>
      }
    >
      {payment.frozen_amount === 0 && (
        <p>
          {t('description', {
            amount: formatCurrency(availableAmount),
          })}
        </p>
      )}
      <Divider className="my-3" />
      {renderFeedbackLabel}
      <Form layout="horizontal">
        <Form.Item label={t('form.amount.label')}>
          <CurrencyInput value={amount} onChange={setAmount} currency={company?.currency} />
        </Form.Item>
        <Form.Item label={t('form.reason.label')}>
          <Select options={reasons} value={reason} onChange={(value) => setReason(value)} />
        </Form.Item>
        {reason === RefundReason.Other && (
          <Form.Item label={t('form.manual_reason.label')}>
            <Input.TextArea
              value={manualReason}
              rows={4}
              maxLength={255}
              resize={false}
              autoFocus
              onChange={(e) => setManualReason(e.target.value)}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
